import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Mail, Lock, User, Phone, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";

export default function RegisterPage() {
    const { register, user } = useAuth();
    const { t } = useTranslation();
    const nav = useNavigate();
    const [form, setForm] = useState({
        firstname: "", lastname: "", email: "", password: "", phone: "", accepted_dsgvo: false,
    });
    const [busy, setBusy] = useState(false);

    if (user && typeof user === "object") {
        nav("/dashboard/generator", { replace: true });
    }

    const upd = (k) => (e) =>
        setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.accepted_dsgvo) {
            toast.error("Bitte akzeptiere die AGB und die Datenschutzerklärung.");
            return;
        }
        setBusy(true);
        const res = await register(form);
        setBusy(false);
        if (res.ok) {
            trackEvent("sign_up");
            toast.success(t("auth.welcomeNew"));
            nav("/dashboard/generator");
        } else {
            toast.error(res.error);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <Seo title="Konto erstellen | Bewerbung24.ai" description="Kostenlos registrieren und die erste Bewerbung mit KI erstellen — ohne Kreditkarte." path="/registrieren" noindex />
            <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "var(--gradient-primary)" }}>
                <Link to="/" className="flex items-center gap-2 text-white">
                    <span className="b24-logo-shine"><img src="/logo-icon.png" alt="bewerbung.ai" className="h-9 w-auto object-contain bg-white/10 rounded-lg p-1" /></span>
                    <span className="font-heading font-bold tracking-tight b24-shine-white">Bewerbung24.ai</span>
                </Link>
                <div className="relative">
                    <div className="absolute -top-32 -right-12 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
                    <h2 className="font-heading text-4xl xl:text-5xl text-white font-bold tracking-tight leading-tight">
                        {t("auth.registerLeftTitle")}
                    </h2>
                    <ul className="mt-8 space-y-3 text-white/90">
                        {["b1", "b2", "b3", "b4"].map((k) => (
                            <li key={k} className="flex items-center gap-2"><Check size={18} className="text-emerald-300" /> {t(`auth.registerBenefits.${k}`)}</li>
                        ))}
                    </ul>
                </div>
                <p className="text-white/70 text-sm">12.000+ {t("home.testimonials.h2Highlight")}</p>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <div className="flex items-center justify-between mb-8">
                        <Link to="/" className="lg:hidden flex items-center gap-2">
                            <span className="b24-logo-shine"><img src="/logo-icon.png" alt="bewerbung.ai" className="h-9 w-auto object-contain" /></span>
                            <span className="font-heading font-bold tracking-tight"><span className="b24-shine-dark">Bewerbung24</span><span className="b24-shine-red">.ai</span></span>
                        </Link>
                        <div className="ml-auto"><LanguageSwitcher /></div>
                    </div>

                    <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">{t("auth.registerTitle")}</h1>
                    <p className="mt-2 text-slate-600">{t("auth.registerSubtitle")} <Link to="/login" className="text-red-600 font-semibold hover:underline">{t("auth.loginLink")}</Link></p>

                    <form className="mt-8 space-y-4" onSubmit={submit} data-testid="register-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm font-medium text-slate-700">{t("common.firstname")}</label>
                                <div className="mt-1 relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input required data-testid="register-firstname-input" value={form.firstname} onChange={upd("firstname")}
                                        className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none" />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-slate-700">{t("common.lastname")}</label>
                                <input required data-testid="register-lastname-input" value={form.lastname} onChange={upd("lastname")}
                                    className="w-full mt-1 px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none" />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700">{t("common.email")}</label>
                            <div className="mt-1 relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input type="email" required data-testid="register-email-input" value={form.email} onChange={upd("email")}
                                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none"
                                    placeholder="email@example.com" />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700">{t("auth.phoneOptional")}</label>
                            <div className="mt-1 relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input type="tel" data-testid="register-phone-input" value={form.phone} onChange={upd("phone")}
                                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none" />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700">{t("common.password")}</label>
                            <div className="mt-1 relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input type="password" required minLength={6} data-testid="register-password-input" value={form.password} onChange={upd("password")}
                                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none"
                                    placeholder={t("auth.minChars")} />
                            </div>
                        </div>
                        <label className="flex items-start gap-2 text-sm text-slate-600">
                            <input type="checkbox" required data-testid="register-dsgvo-checkbox" checked={form.accepted_dsgvo} onChange={upd("accepted_dsgvo")} className="mt-1" />
                            <span>
                                Ich akzeptiere die{" "}
                                <Link to="/agb" target="_blank" data-testid="register-terms-link" className="text-red-600 font-semibold hover:underline">AGB</Link>
                                {" "}und die{" "}
                                <Link to="/datenschutz" target="_blank" data-testid="register-privacy-link" className="text-red-600 font-semibold hover:underline">Datenschutzerklärung</Link>
                                {" "}und stimme der Verarbeitung meiner Daten zu (DSGVO).
                            </span>
                        </label>
                        <button type="submit" disabled={busy} data-testid="register-submit-button"
                            className="w-full py-3 rounded-full font-semibold btn-primary-grad disabled:opacity-60">
                            {busy ? t("auth.registerBusy") : t("auth.submitRegister")}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
